/** The tenant's catalogue settings (`config.catalogue.<restech>`, plan §3.3.6): which entities
 *  the sync may create and AI-update, what happens to AI-built outputs when their product
 *  changes upstream, the review digest alerts, and the registry-driven per-product defaults.
 *
 *  Saved with the GET's `config_last_modified_ts` as `if_unmodified_since`; a 409
 *  `config_modified` says someone saved meanwhile and offers a reload. A franchise licensee
 *  without its own section inherits its parent's: the first save copies it (copy-on-write),
 *  so the form asks before forking. Needs `console_top_manage` to save. A default that would take
 *  the one tour away from products others join is refused `join_target_in_use`, one error per such
 *  product: they are listed by name with their joiners (`TargetsInUseAlert`). */
import { zodResolver } from '@hookform/resolvers/zod';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import * as catalogueApi from '@/api/catalogue';
import {
  catalogueKeys,
  type CatalogueRegistry,
  type SettingError,
  type TenantSettings,
  type TenantSettingsPatch,
  type UpstreamPolicy,
} from '@/api/catalogue';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldHelp } from '@/components/FieldHelp';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { defaultLabel, groupLabel, mapSettingErrors, TARGET_IN_USE, valueLabel } from './catalogueModel';
import { TargetsInUseAlert } from './JoinNotes';
import { CATALOGUE_STORY } from './help';
import { useInvalidateCatalogue } from './queries';
import { SettingControl } from './SettingControl';

const POLICIES: Array<{ value: UpstreamPolicy; label: string; detail: string }> = [
  {
    value: 'review',
    label: 'Raise with ops',
    detail: 'Hold the change in the Review queue (and the digest email, when alerts are on). Default.',
  },
  {
    value: 'apply',
    label: 'Apply automatically',
    detail: 'Rebuild the AI-built tours, itineraries and activities of a changed product without asking.',
  },
  { value: 'ignore', label: 'Ignore', detail: 'Record the change and keep what was built.' },
];

/** Exactly what the controls produce — never stricter (lessons: a stricter schema makes Save a
 *  silent no-op). Emails and the interval range are validated by the server, onto the fields. */
const schema = z.object({
  tours: z.boolean(),
  itineraries: z.boolean(),
  policy: z.enum(['apply', 'ignore', 'review']),
  alertsEnabled: z.boolean(),
  emails: z.array(z.string()),
  interval: z.string(),
  comment: z.string(),
});
type FormValues = z.infer<typeof schema>;

function valuesOf(ts: TenantSettings): FormValues {
  const e = ts.effective;
  return {
    tours: e.ai_sync.tours,
    itineraries: e.ai_sync.itineraries,
    policy: e.on_upstream_change,
    alertsEnabled: e.alerts.enabled ?? false,
    emails: [...(e.alerts.emails ?? [])],
    interval: String(e.alerts.min_interval_hours ?? 24),
    comment: '',
  };
}

function omit(d: Record<string, unknown>, key: string): Record<string, unknown> {
  const next = { ...d };
  delete next[key];
  return next;
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/** The PATCH body of what changed: only changed sections and keys travel. */
export function tenantPatchOf(
  initial: FormValues,
  v: FormValues,
  defaults: Record<string, unknown>,
  lastModified: string | null,
): TenantSettingsPatch | null {
  const body: TenantSettingsPatch = { if_unmodified_since: lastModified };
  const ai: NonNullable<TenantSettingsPatch['ai_sync']> = {};
  if (v.tours !== initial.tours) ai.tours = v.tours;
  if (v.itineraries !== initial.itineraries) ai.itineraries = v.itineraries;
  if (Object.keys(ai).length) body.ai_sync = ai;
  if (v.policy !== initial.policy) body.on_upstream_change = v.policy;
  const alerts: NonNullable<TenantSettingsPatch['alerts']> = {};
  if (v.alertsEnabled !== initial.alertsEnabled) alerts.enabled = v.alertsEnabled;
  if (!sameList(v.emails, initial.emails)) alerts.emails = v.emails.length ? v.emails : null;
  if (v.interval !== initial.interval) {
    const n = Number(v.interval);
    // Sent as typed when it is no whole number: the server refuses it onto the field.
    alerts.min_interval_hours = (Number.isInteger(n) ? n : v.interval) as number;
  }
  if (Object.keys(alerts).length) body.alerts = alerts;
  if (Object.keys(defaults).length) body.defaults = { ...defaults };
  if (Object.keys(body).length === 1) return null;
  if (v.comment.trim()) body.comment = v.comment.trim();
  return body;
}

/** A default's field when products others join would lose their tour (the alert names them). */
const IN_USE_FIELD = 'Products others join would lose their tour with this default: see above.';

/** Server paths → form fields. */
const PATH_FIELDS: Record<string, keyof FormValues> = {
  'ai_sync.tours': 'tours',
  'ai_sync.itineraries': 'itineraries',
  on_upstream_change: 'policy',
  'alerts.enabled': 'alertsEnabled',
  'alerts.emails': 'emails',
  'alerts.min_interval_hours': 'interval',
  comment: 'comment',
};

export function TenantSettingsForm({
  tenantId,
  restech,
  registry,
}: {
  tenantId: number;
  restech: string;
  registry: CatalogueRegistry;
}) {
  const query = useQuery({
    queryKey: catalogueKeys.tenantSettings(tenantId, restech),
    queryFn: ({ signal }) => catalogueApi.getTenantSettings(tenantId, restech, signal),
    retry: false,
  });
  if (query.isLoading) return <LinearProgress aria-label="Loading the catalogue settings" />;
  if (query.isError || !query.data) {
    return <Alert severity="error">{query.error ? (query.error as Error).message : 'No settings.'}</Alert>;
  }
  return (
    <TenantSettingsBody
      tenantId={tenantId}
      restech={restech}
      registry={registry}
      data={query.data}
      reload={() => void query.refetch()}
    />
  );
}

/** Mounted once the settings are in hand, so the form starts from them. */
function TenantSettingsBody({
  tenantId,
  restech,
  registry,
  data,
  reload,
}: {
  tenantId: number;
  restech: string;
  registry: CatalogueRegistry;
  data: TenantSettings;
  reload: () => void;
}) {
  const { show } = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_top_manage');
  const invalidate = useInvalidateCatalogue(tenantId);
  const initial = useMemo(() => valuesOf(data), [data]);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: initial });
  const values = useWatch({ control: form.control }) as FormValues;
  const [defaults, setDefaults] = useState<Record<string, unknown>>({});
  const [defaultErrors, setDefaultErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string[]>([]);
  // A default refused because products join a product it reaches (join_target_in_use).
  const [inUse, setInUse] = useState<SettingError[]>([]);
  const [conflict, setConflict] = useState<string | null>(null);
  const [confirmFork, setConfirmFork] = useState(false);

  // Every fresh read (reload after a conflict, after a save) restarts the form from it.
  useEffect(() => {
    form.reset(initial);
    setDefaults({});
    setDefaultErrors({});
    setGeneral([]);
    setInUse([]);
  }, [initial, form]);

  const tenantDefaultSpecs = useMemo(
    () => registry.settings.filter((s) => s.tenant_default && s.kind === 'policy'),
    [registry],
  );
  const current: FormValues = { ...initial, ...values };
  const patch = tenantPatchOf(initial, current, defaults, data.config_last_modified_ts);

  const mutation = useMutation({
    mutationFn: (body: TenantSettingsPatch) => catalogueApi.patchTenantSettings(tenantId, restech, body),
    onMutate: () => {
      setGeneral([]);
      setInUse([]);
      setDefaultErrors({});
      setConflict(null);
      form.clearErrors();
    },
    onSuccess: async (r) => {
      show(
        !r.changed
          ? 'Nothing changed.'
          : r.forked_from_parent
            ? `Saved. This tenant now has its own catalogue settings (copied from ${r.forked_from_parent.code}).`
            : 'Catalogue settings saved.',
        'success',
      );
      await invalidate();
    },
    onError: (e) => {
      if (catalogueApi.isConfigModified(e)) {
        setConflict((e as Error).message);
        return;
      }
      const paths = catalogueApi.fieldErrorsOf(e);
      if (paths) {
        const rest: string[] = [];
        for (const [path, msg] of Object.entries(paths)) {
          const field = PATH_FIELDS[path];
          if (field) form.setError(field, { message: msg });
          else rest.push(`${path}: ${msg}`);
        }
        setGeneral(rest);
        return;
      }
      const refusals = catalogueApi.settingErrorsOf(e);
      if (refusals?.length) {
        // join_target_in_use: one per product others join, listed above the form by name; its
        // default's field points there.
        const targets = refusals.filter((r) => r.code === TARGET_IN_USE);
        const mapped = mapSettingErrors(refusals.filter((r) => r.code !== TARGET_IN_USE));
        for (const r of targets) {
          if (r.key && !mapped.fields[r.key]) mapped.fields[r.key] = IN_USE_FIELD;
        }
        setDefaultErrors(mapped.fields);
        setGeneral(mapped.general);
        setInUse(targets);
        return;
      }
      setGeneral([(e as Error).message]);
    },
  });

  const submit = () => {
    if (!patch) return;
    if (data?.inherited_from) setConfirmFork(true);
    else mutation.mutate(patch);
  };

  const err = (f: keyof FormValues) => form.formState.errors[f]?.message;
  const readOnly = !canManage || mutation.isPending;
  const stored = data.effective.defaults;

  return (
    <Box component="form" onSubmit={form.handleSubmit(submit)} sx={{ maxWidth: 920 }} aria-label="Catalogue settings">
      {data.inherited_from && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Inherited from {data.inherited_from.name} ({data.inherited_from.code})</AlertTitle>
          This tenant has no catalogue settings of its own: it follows its franchise parent. The first save copies the
          parent&apos;s catalogue settings here and applies the change; from then on this tenant no longer follows the
          parent. The parent is never edited.
        </Alert>
      )}
      {data.problems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stored values that are malformed and fall back to their defaults: <code>{data.problems.join(', ')}</code>. Saving
          a value here replaces them.
        </Alert>
      )}
      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only: changing the tenant&apos;s catalogue settings needs the <code>console_top_manage</code> permission.
        </Alert>
      )}
      {conflict && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setConflict(null); reload(); }}>
              Reload
            </Button>
          }
        >
          <AlertTitle>The configuration changed since this form loaded</AlertTitle>
          {conflict} Nothing was saved. Reload to start from the saved settings — the edits here are discarded.
        </Alert>
      )}
      <TargetsInUseAlert tenantId={tenantId} errors={inUse} />
      {general.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Nothing was saved</AlertTitle>
          {general.map((g, i) => (
            <div key={i}>{g}</div>
          ))}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        What the sync may create and AI-update
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Controller
          control={form.control}
          name="tours"
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />}
              label="Tours"
            />
          )}
        />
        <Controller
          control={form.control}
          name="itineraries"
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />}
              label="Itinerary cards (Explore)"
            />
          )}
        />
      </Box>
      {!current.tours && (
        <Alert severity="warning" sx={{ my: 1 }}>
          With tours off, catalogue syncs create and update no tours: bookings only import once ops create or map tours by
          hand. Existing synced tours stay as they are.
        </Alert>
      )}
      {!current.itineraries && (
        <Typography variant="caption" color="text.secondary" component="div">
          With itinerary cards off, syncs create no Explore cards; existing ones stay as they are.
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        When a synced product changes upstream
        <FieldHelp
          help={{
            title: 'Upstream changes',
            body: 'Applies to AI-built outputs only, when the change is found outside an explicit ops sync (a listing refresh). Deterministic outputs are applied automatically; decoupled ones are never touched.',
            story: CATALOGUE_STORY,
          }}
        />
      </Typography>
      <Controller
        control={form.control}
        name="policy"
        render={({ field }) => (
          <RadioGroup value={field.value} onChange={(e) => field.onChange(e.target.value)} aria-label="Upstream change policy">
            {POLICIES.map((p) => (
              <FormControlLabel
                key={p.value}
                value={p.value}
                disabled={readOnly}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">{p.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.detail}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.5 } }}
              />
            ))}
          </RadioGroup>
        )}
      />
      {err('policy') && <Typography color="error" variant="caption">{err('policy')}</Typography>}

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
        Review digest email
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Controller
          control={form.control}
          name="alertsEnabled"
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />}
              label="Send a digest of products added to the review queue"
            />
          )}
        />
        <Controller
          control={form.control}
          name="interval"
          render={({ field }) => (
            <TextField
              size="small"
              type="number"
              label="At most every (hours)"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              disabled={readOnly || !current.alertsEnabled}
              error={Boolean(err('interval'))}
              helperText={err('interval')}
              inputProps={{ min: 1, max: 8760 }}
              sx={{ width: 190 }}
            />
          )}
        />
      </Box>
      <Controller
        control={form.control}
        name="emails"
        render={({ field }) => (
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={field.value}
            onChange={(_, v) => field.onChange(v.map((x) => String(x).trim()).filter(Boolean))}
            disabled={readOnly || !current.alertsEnabled}
            renderTags={(vals, getTagProps) =>
              vals.map((v, i) => {
                const { key, ...tag } = getTagProps({ index: i });
                return <Chip key={key} size="small" label={v} {...tag} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Recipients"
                placeholder={field.value.length ? '' : "Empty = the tenant's error owners"}
                error={Boolean(err('emails'))}
                helperText={err('emails') ?? `Now: ${data.alert_recipients.join(', ') || 'nobody'}`}
              />
            )}
            sx={{ mt: 1, maxWidth: 640 }}
          />
        )}
      />

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>
        Defaults for this tenant&apos;s products
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Used by every product without an ops override of its own. A changed default reaches all of them on their next
        sync.
      </Typography>
      {tenantDefaultSpecs.map((spec) => {
        const has = Object.prototype.hasOwnProperty.call(defaults, spec.key);
        const pending = has ? defaults[spec.key] : undefined;
        const isSet = has ? pending !== null : spec.key in stored;
        const value = has ? (pending === null ? spec.default : pending) : spec.key in stored ? stored[spec.key] : spec.default;
        return (
          <Box
            key={spec.key}
            data-default={spec.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '220px 1fr auto' },
              gap: 1.5,
              alignItems: 'start',
              py: 0.75,
              borderBottom: 1,
              borderColor: 'divider',
              ...(has ? { bgcolor: 'rgba(237, 108, 2, 0.06)' } : {}),
            }}
          >
            <Box sx={{ pt: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {spec.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {groupLabel(spec.group)}
                {spec.applies_rule ? ` · ${spec.applies_rule}` : ''}
              </Typography>
            </Box>
            <Box>
              <SettingControl
                spec={spec}
                value={value}
                onChange={(v) => setDefaults((d) => ({ ...d, [spec.key]: v }))}
                error={defaultErrors[spec.key]}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" component="div">
                {isSet
                  ? `Tenant default${has ? ' (unsaved)' : ''}. Without it: ${defaultLabel(spec)}.`
                  : `Not set — the integration's default applies: ${defaultLabel(spec)}.`}
              </Typography>
            </Box>
            <Box sx={{ pt: 0.5 }}>
              {has ? (
                <Tooltip title="Undo this change">
                  <Button size="small" onClick={() => setDefaults((d) => omit(d, spec.key))} disabled={readOnly}>
                    Undo
                  </Button>
                </Tooltip>
              ) : spec.key in stored ? (
                <Tooltip title={`Remove the tenant default (back to ${valueLabel(spec, spec.default) || 'the integration default'})`}>
                  <IconButton
                    size="small"
                    aria-label={`Remove the tenant default of ${spec.label}`}
                    onClick={() => setDefaults((d) => ({ ...d, [spec.key]: null }))}
                    disabled={readOnly}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2.5, flexWrap: 'wrap' }}>
        <Controller
          control={form.control}
          name="comment"
          render={({ field }) => (
            <TextField
              size="small"
              label="Change description (optional)"
              value={field.value}
              onChange={field.onChange}
              disabled={readOnly}
              inputProps={{ maxLength: 500 }}
              sx={{ flex: '1 1 320px' }}
            />
          )}
        />
        <Button
          onClick={() => {
            form.reset(initial);
            setDefaults({});
          }}
          disabled={!patch || mutation.isPending}
        >
          Discard
        </Button>
        <Button type="submit" variant="contained" disabled={!patch || readOnly}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmFork}
        title="Give this tenant its own catalogue settings?"
        message={`Saving copies ${data.inherited_from?.name ?? 'the parent'}'s catalogue settings into this tenant and applies your change there. From then on this tenant no longer follows the parent's catalogue settings; the parent is not changed.`}
        confirmLabel="Copy and save"
        onCancel={() => setConfirmFork(false)}
        onConfirm={() => {
          setConfirmFork(false);
          if (patch) mutation.mutate(patch);
        }}
      />
    </Box>
  );
}
